/**
 * Pulse wall — shared foundation (band tokens, classification, public types).
 *
 * Extracted from `pulse-wall.tsx` (#1144): the band semantics + the per-band
 * instrumentation-state logic + the public `Pulse*` types, so the wall renderer
 * and its sibling sub-views (`pulse-wall-cells`, `pulse-wall-tips`,
 * `pulse-wall-breakdown`) all source ONE copy instead of forking it. Pure data
 * + logic, no JSX — the `.ts` here is deliberate (the sub-views import these into
 * their own React modules). LOCKED visual language lives in `pulse-wall.tsx` §0.
 */

import type {
  PulseGroupDTO,
  PulseTouchDTO,
  PulseWeight,
} from "../../api/contracts/pulse";

/** The mode slug a scope addresses (`"customers"` from `"customers:sales"`). */
export function modeOfScope(scope: string): string {
  return scope.split(":")[0] ?? scope;
}

// ── band tokens (sourced locally; matches `_shared.tsx` BAND, §0) ─────────────

export type Band = "ok" | "watch" | "risk";

/** Watch floor as a fraction of target — mirrors the engine's `band()` split. */
export const WATCH_RATIO = 45 / 70;

/** Band a health against its (tuned) target — bottleneck = below target (§0). */
export function band(health: number, target = 70): Band {
  if (health >= target) return "ok";
  if (health >= target * WATCH_RATIO) return "watch";
  return "risk";
}

export const BAND: Record<Band, { label: string; bg: string; fg: string; soft: string; border: string }> = {
  ok: { label: "Healthy", bg: "bg-success", fg: "text-success", soft: "bg-success/10", border: "border-success/30" },
  watch: { label: "Watch", bg: "bg-warning", fg: "text-warning", soft: "bg-warning/10", border: "border-warning/30" },
  risk: { label: "At-risk", bg: "bg-danger", fg: "text-danger", soft: "bg-danger/10", border: "border-danger/30" },
};

// ── the shipped Pulse paint: a dark glow instrument ───────────────────────────
//
// The wall is one always-dark stage (so the glow reads in any app theme). Healthy
// bars glow GREEN; a low bar runs a warm SEVERITY ramp (orange just below target →
// red → deep red at critical) so you read HOW bad at a glance; a blank bar is a
// faint white (no signal). The halo is computed once and never flares on hover —
// hover focus comes from the section lift + neighbours receding, kept cheap. This
// is pure colour math (no JSX) so every wall surface — the cells, the breakdown,
// the home + nav mirrors — paints from ONE source. `band()`/`BAND` above stay for
// the engine classification + the semantic text chips.

/** The always-dark wall stage (identical in light + dark theme — a deliberate
 *  instrument panel, the only way the glow reads on a light app surface). */
export const PULSE_STAGE = "#161719";
/** Faint-white ghost opacity (a blank bar on the dark stage). */
export const PULSE_GHOST_OPACITY = 0.1;

const PULSE_HEALTHY_RGB = "52,211,153"; // emerald — at/above target
const PULSE_GHOST_RGB = "255,255,255"; //  faint white — no signal
type Rgb = [number, number, number];
const PULSE_RAMP: [Rgb, Rgb, Rgb] = [
  [251, 146, 60], // mild     — orange (just below target)
  [239, 68, 68], //  low      — red
  [176, 22, 22], //  critical — deep red
];

function pulseRampRgb(severity: number): string {
  const s = severity < 0 ? 0 : severity > 1 ? 1 : severity;
  const a: Rgb = s < 0.5 ? PULSE_RAMP[0] : PULSE_RAMP[1];
  const b: Rgb = s < 0.5 ? PULSE_RAMP[1] : PULSE_RAMP[2];
  const t = s < 0.5 ? s / 0.5 : (s - 0.5) / 0.5;
  return `${Math.round(a[0] + (b[0] - a[0]) * t)},${Math.round(a[1] + (b[1] - a[1]) * t)},${Math.round(
    a[2] + (b[2] - a[2]) * t,
  )}`;
}

/** A bar's colour as an "r,g,b" string: emerald at/above target, else the warm
 *  ramp by how far below target it sits (deeper below ⇒ hotter). */
export function pulseToneRgb(health: number, target: number): string {
  if (health >= target) return PULSE_HEALTHY_RGB;
  return pulseRampRgb((target - health) / Math.max(target, 1));
}

/** The glow paint for ONE bar — a vertical gradient fill + a soft, steady halo.
 *  `health == null` ⇒ a flat faint-white ghost (no halo). Returned as inline-style
 *  fields because the colour is data-driven (a ramp), not a fixed token class. */
export function pulseBarFill(
  health: number | null,
  target: number,
  ghostOpacity: number = PULSE_GHOST_OPACITY,
): { background: string; boxShadow?: string } {
  if (health == null) return { background: `rgba(${PULSE_GHOST_RGB},${ghostOpacity})` };
  const rgb = pulseToneRgb(health, target);
  const base = health >= target ? 0.9 : 0.95;
  return {
    background: `linear-gradient(to top, rgba(${rgb},${base * 0.72}), rgba(${rgb},${base}))`,
    boxShadow: `0 0 5px rgba(${rgb},0.26), 0 -2px 7px rgba(${rgb},0.18)`,
  };
}

/** A blank (uninstrumented) bar renders as JUST a faint hairline ring — an empty
 *  slot that recedes on the dark stage, not a full-height ghost block — and fills
 *  with its ghost-white colour (`pulseBarFill(null).background`) on hover. ONE
 *  source for both wall renderers: the shared cells + the home granular mirror. */
export const PULSE_BAR_RADIUS = 6;
export const PULSE_GHOST_RING = "inset 0 0 0 1px rgba(255,255,255,0.10)";

export type PulseSize = "full" | "strip" | "mini";

/**
 * The wall's bar-drawing view mode (docs/lifecycle-spine/PRD.md §0, §11 P6).
 *   · "house"     — the LOCKED house style: vertical bars + a benchmark line.
 *   · "diverging" — the optional analytics view: each bar drawn from a CENTER
 *     baseline, growing UP (green) when health ≥ target or DOWN (red) when
 *     below, by the magnitude of the deviation. A pure client view-mode of the
 *     SAME data (no new fetch) — toggled on the full wall only, behind the
 *     editable/analytics affordance.
 */
export type PulseViewMode = "house" | "diverging";

export interface PulseProps {
  /** The scope path: a mode (`"customers"`), `"workspace"`, `"<mode>:<area>"`,
   *  or `"project:<slug>"` (a project's section rollup). */
  scope: string;
  /** "full" (mode dashboard) · "strip" (atop a list) · "mini" (one entity). */
  size?: PulseSize;
  /** Rollup altitude (SECTION-PULSE-PRD §2). `"sections"` renders ONE bar per
   *  section (the section-aggregate home); absent ⇒ the per-touchpoint wall. The
   *  `project:<slug>` scope is always a section rollup, so this is the mode-scope
   *  selector. */
  rollup?: "sections";
  /** Aggregation weighting override (default: the provider's). */
  weight?: PulseWeight;
  /** Trend window in days (default: the engine's 30). */
  days?: number;
  /** Editors get the inline target-tune chips + the count/value weight toggle
   *  (docs/lifecycle-spine/PRD.md §6.3). Default false (read-only). */
  editable?: boolean;
  /** Project tier (Pulse Graph Stage 1 fix): the project this wall is scoped to,
   *  so a band-dive stays inside the project (`/projects/<slug>/<mode>?area=…`)
   *  instead of leaving to the workspace tier. Forwarded to the route as a query
   *  param. Absent ⇒ workspace tier (the band-dive uses `domainHomeRoute`). */
  projectSlug?: string;
  /**
   * The current sub-page's section (its `domain.area` or group `key`). On the
   * low-profile strip this draws a subtle "you are here" ring on the matching
   * card — and ONLY that: every other card stays at full strength (no dim, no
   * recede). It's the quiet-spine "where am I" marker, distinct from the retired
   * `emphasize` treatment, which spotlit one band AND de-emphasized the rest.
   * Absent (the mode-root hero, or a strip with no active section) ⇒ no marker.
   */
  current?: string;
  className?: string;
}

/** A live (non-ghost) touch with a numeric health. */
export type LiveTouch = PulseTouchDTO & { health: number };

export function isLive(t: PulseTouchDTO): t is LiveTouch {
  return t.health != null;
}

/** A SUGGESTED (draft) bar — the third bar state (§0, §9.3): a draft flow that
 *  WOULD instrument this band (health:null + a `+ activate` cta), distinct from a
 *  planned ghost (no candidate) and a live bar. Appended server-side. */
export type SuggestedTouch = PulseTouchDTO & { suggested: NonNullable<PulseTouchDTO["suggested"]> };

export function isSuggested(t: PulseTouchDTO): t is SuggestedTouch {
  return t.suggested != null;
}

// ── band instrumentation state + the "light this band" decision (§9.3) ────────
//
// Pure, block-side classification from the data already on the wire (touch
// `kind`/`health` + `group.domain`) — no new recommender, no server field. A
// band is `uninstrumented` when NO bar is live (all planned ghosts ± suggested
// drafts), `partial` when some live + some planned remain, else `instrumented`.

export type BandState = "instrumented" | "partial" | "uninstrumented";

export function bandState(g: PulseGroupDTO): BandState {
  const live = g.touches.filter(isLive).length;
  if (live === 0) return "uninstrumented";
  const planned = g.touches.filter((t) => t.health == null && !isSuggested(t)).length;
  return planned > 0 ? "partial" : "instrumented";
}

/** What it takes to light a band that isn't fully live — the prompt the user
 *  asked for. An all-ghost band whose bars depend on a connected system →
 *  `connect` (→ /integrations); a stage/pipeline band → `enable` (→ its domain
 *  home); otherwise a draft flow would light it → `activate`. A band that is
 *  fully live, or all-ghost with nothing buildable, returns null (honest ghosts,
 *  no fabricated CTA — §12.2). */
export type BandLightKind = "connect" | "enable" | "activate";

export function bandLight(g: PulseGroupDTO): { kind: BandLightKind; href?: string } | null {
  const hasDraft = g.touches.some(isSuggested);
  if (bandState(g) === "uninstrumented") {
    const kinds = new Set(g.touches.map((t) => t.kind));
    if (kinds.has("integration") || kinds.has("channel")) return { kind: "connect", href: "/integrations" };
    if (kinds.has("stage")) return { kind: "enable", href: `/${g.domain.mode}?area=${g.domain.area}` };
    return hasDraft ? { kind: "activate" } : null;
  }
  // A live band with a draft candidate is pure upside (still worth surfacing).
  return hasDraft ? { kind: "activate" } : null;
}
